import { useEffect, useState, useRef, useMemo } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "./components/LanguageSwitcher";
import logo from "../assets/photo_2026-04-06 22.58.14.jpeg";
import { getUnreadCount } from "../data/messages";
import { auth as fbAuth } from "../firebase";

const API_BASE =
  process.env.REACT_APP_API_URL ||
  "http://localhost:3001";

function UnreadBadge({ count }) {
  const n = Number(count || 0);
  if (!n) return null;
  const label = n > 9 ? "9+" : String(n);
  return (
    <span className="ml-2 inline-flex min-w-[20px] items-center justify-center rounded-full bg-[#7269e3] px-2 py-0.5 text-[11px] font-semibold text-white">
      {label}
    </span>
  );
}

function HamburgerIcon({ open }) {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    >
      {open ? (
        <>
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </>
      ) : (
        <>
          <line x1="3" y1="7" x2="21" y2="7" />
          <line x1="3" y1="12" x2="21" y2="12" />
          <line x1="3" y1="17" x2="21" y2="17" />
        </>
      )}
    </svg>
  );
}

function readAuthLS() {
  try {
    return JSON.parse(localStorage.getItem("auth") || "null");
  } catch {
    return null;
  }
}

function getTokenLS() {
  try {
    const a = JSON.parse(localStorage.getItem("auth") || "null");
    return a?.token || null;
  } catch {
    return null;
  }
}

export default function Navbar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [auth, setAuth] = useState(null);
  // user-avatar dropdown (desktop)
  const [menuOpen, setMenuOpen] = useState(false);
  // mobile drawer
  const [mobileOpen, setMobileOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);

  const [fbReady, setFbReady] = useState(false);
  const [fbUser, setFbUser] = useState(null);
  const [avatarUrl, setAvatarUrl] = useState("");
  // Tracks whether the current avatarUrl actually failed to load as an image
  // (e.g. the file was wiped server-side, or the URL is stale). Without this,
  // a broken avatarUrl renders the browser's native broken-image icon (looks
  // like a blank box with a "?") instead of falling back to initials.
  const [avatarLoadFailed, setAvatarLoadFailed] = useState(false);

  const menuRef   = useRef(null);
  const buttonRef = useRef(null);

  // Firebase session listener
  useEffect(() => {
    const unsub = fbAuth.onAuthStateChanged(async (u) => {
      setFbReady(true);
      if (u) {
        const isEmailProvider = u.providerData.some(
          (p) => p.providerId === "password"
        );
        if (isEmailProvider && !u.emailVerified) {
          // Unverified email accounts: clear stale localStorage so
          // ProtectedRoute redirects to /login, but keep the Firebase
          // session alive so Signup.jsx can still call firebaseUser.reload().
          localStorage.removeItem("auth");
          localStorage.removeItem("isAuthed");
          setAuth(null);
          setFbUser(null);
        } else {
          setFbUser(u);
          // If Firebase says the user is authenticated (email verified or
          // phone auth) but localStorage has no auth data, the user probably
          // verified their email by clicking the link in a different tab/app,
          // which navigated away from the SPA and lost React state. Auto-fetch
          // /api/auth/me so they land on the correct role dashboard without
          // having to log in again manually.
          if (!localStorage.getItem("auth")) {
            try {
              const token = await u.getIdToken(true);
              const res = await fetch(`${API_BASE}/api/auth/me`, {
                headers: { Authorization: `Bearer ${token}` },
              });
              if (res.ok) {
                const data = await res.json();
                const user = data.user ?? data;
                const authPayload = {
                  id:    user.id,
                  email: user.email,
                  role:  user.role,
                  name:  user.name,
                  token,
                };
                localStorage.setItem("auth", JSON.stringify(authPayload));
                localStorage.setItem("isAuthed", "1");
                setAuth(authPayload);
              }
            } catch (_) {
              // Non-critical: user will just need to log in manually
            }
          }
        }
      } else {
        setFbUser(null);
        // Firebase has definitively resolved with NO session. Any auth data
        // left in localStorage is stale (e.g. the Firebase session expired or
        // was revoked after a previous login). Without this cleanup, every
        // page load flashes the logged-in "User" chip and the mobile bottom
        // nav renders the authed tabs for visitors who are actually signed
        // out — and the unread-count poller keeps hitting the API anonymously.
        if (localStorage.getItem("auth") || localStorage.getItem("isAuthed")) {
          localStorage.removeItem("auth");
          localStorage.removeItem("isAuthed");
          setAuth(null);
          // Let other components that mirror auth state (MobileBottomNav)
          // know immediately, without waiting for a route change.
          window.dispatchEvent(new Event("profio:auth-cleared"));
        }
      }
    });
    return () => unsub();
  }, []);

  // Read auth on navigation; close mobile drawer on route change
  useEffect(() => {
    setAuth(readAuthLS());
    setMobileOpen(false);

    // Re-sync the Firebase user snapshot. onAuthStateChanged fires once on
    // initial load — if the email wasn't verified yet at that moment, fbUser
    // gets set to null and Firebase won't refire the listener just because
    // emailVerified flips to true after the user confirms their email and
    // Signup.jsx calls firebaseUserRef.reload(). Without this, the navbar
    // stays stuck showing "Sign in" even though localStorage.auth is valid.
    const cu = fbAuth.currentUser;
    if (cu) {
      const isEmailProvider = cu.providerData.some((p) => p.providerId === "password");
      if (!isEmailProvider || cu.emailVerified) {
        setFbUser(cu);
      }
    }
  }, [location.pathname]);

  // Close desktop dropdown on outside click / ESC
  useEffect(() => {
    const onClickOutside = (e) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target)
      ) {
        setMenuOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") {
        setMenuOpen(false);
        setMobileOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClickOutside);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  // Lock body scroll when mobile drawer is open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  // While Firebase is still initialising (fbReady=false), trust localStorage so
  // the navbar doesn't flash "Sign in" for a second on every page load/reload.
  // Once Firebase resolves, fbUser must also be present to stay authenticated.
  const isAuthed = !!(auth?.token && (fbUser || !fbReady));

  const initials = useMemo(() => {
    return (
      auth?.name
        ?.split(" ")
        .map((s) => s[0])
        .join("")
        .slice(0, 2)
        .toUpperCase() || "U"
    );
  }, [auth?.name]);

  const handleLogout = async () => {
    localStorage.removeItem("isAuthed");
    localStorage.removeItem("auth");
    localStorage.removeItem("role");
    localStorage.removeItem("fullName");
    setAuth(null);
    setUnread(0);
    setIsAdmin(false);
    setMenuOpen(false);
    setMobileOpen(false);
    try { await fbAuth.signOut(); } catch {}
    navigate("/login");
  };

  // Poll unread count
  useEffect(() => {
    if (!fbReady) return;
    let alive = true;
    let id = null;
    const tick = async () => {
      try {
        const n = await getUnreadCount();
        if (alive) setUnread(Number(n || 0));
      } catch (e) {
        const status = e?.status;
        if (status === 401 || status === 403) {
          if (alive) setUnread(0);
          if (id) clearInterval(id);
          id = null;
        }
      }
    };
    if (isAuthed) {
      tick();
      id = setInterval(tick, 30000);
    } else {
      setUnread(0);
    }
    return () => { alive = false; if (id) clearInterval(id); };
  }, [isAuthed, fbReady]);

  // Admin check
  useEffect(() => {
    if (!fbReady) return;
    if (!isAuthed) { setIsAdmin(false); return; }
    let alive = true;
    (async () => {
      try {
        const token = getTokenLS();
        if (!token) { if (alive) setIsAdmin(false); return; }
        const res = await fetch(`${API_BASE}/api/admin/whoami`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!alive) return;
        setIsAdmin(res.ok);
      } catch {
        if (alive) setIsAdmin(false);
      }
    })();
    return () => { alive = false; };
  }, [isAuthed, fbReady]);

  // Fetch the user's uploaded profile photo (if any) so the avatar circle
  // can show the real picture instead of just initials. AccountSettings.jsx
  // already does this against the same endpoint.
  useEffect(() => {
    if (!isAuthed) { setAvatarUrl(""); return; }
    let alive = true;
    (async () => {
      try {
        const token = getTokenLS();
        if (!token) return;
        const res = await fetch(`${API_BASE}/api/me/profile`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const d = await res.json();
        if (alive) {
          setAvatarUrl(d?.profile?.avatarUrl || "");
          setAvatarLoadFailed(false);
        }
      } catch {
        if (alive) setAvatarUrl("");
      }
    })();
    return () => { alive = false; };
  }, [isAuthed, location.pathname]);

  // Pick up avatar changes immediately when AccountSettings.jsx saves a new
  // photo, without waiting for a route change (the effect above only re-fetches
  // on [isAuthed, location.pathname], so saving while staying on /account would
  // otherwise leave the navbar showing the old/placeholder avatar).
  useEffect(() => {
    const onAvatarUpdated = (e) => {
      if (e?.detail && "avatarUrl" in e.detail) {
        setAvatarUrl(e.detail.avatarUrl || "");
        setAvatarLoadFailed(false);
      }
    };
    window.addEventListener("profio:avatar-updated", onAvatarUpdated);
    return () => window.removeEventListener("profio:avatar-updated", onAvatarUpdated);
  }, []);

  const absAvatarUrl = (url) => {
    if (!url) return "";
    return url.startsWith("http") ? url : `${API_BASE}${url}`;
  };

  function Avatar({ size = "h-7 w-7", textSize = "text-xs" }) {
    if (avatarUrl && !avatarLoadFailed) {
      return (
        <img
          src={absAvatarUrl(avatarUrl)}
          alt=""
          // crossOrigin so the cross-origin (api.*) avatar renders — a plain
          // <img> is blocked by the API's Cross-Origin-Resource-Policy.
          crossOrigin="anonymous"
          onError={() => setAvatarLoadFailed(true)}
          className={`${size} shrink-0 rounded-full object-cover`}
        />
      );
    }
    return (
      <span className={`grid ${size} shrink-0 place-content-center rounded-full bg-[#7269e3] ${textSize} font-bold text-white`}>
        {initials}
      </span>
    );
  }

  const closeMobile = () => setMobileOpen(false);

  return (
    <>
      <header className="border-b border-white/10 bg-[#272c39] relative z-40">
        <div className="mx-auto flex max-w-6xl items-center gap-4 px-4 py-4">
          {/* Logo */}
          <Link to="/" className="shrink-0" aria-label="Profio">
            <img src={logo} alt="Profio" className="h-11 w-auto" />
          </Link>

          {/* ── Desktop nav ─────────────────────────────────────────── */}
          <nav className="ml-auto hidden md:flex items-center gap-6">
            <Link to="/about" className="text-white/80 hover:text-white transition">
              {t("navbar.about")}
            </Link>
            <Link to="/contact" className="text-white/80 hover:text-white transition">
              {t("navbar.contact")}
            </Link>

            <LanguageSwitcher />

            {isAuthed ? (
              <div className="relative" ref={menuRef}>
                <button
                  ref={buttonRef}
                  onClick={() => setMenuOpen((p) => !p)}
                  className="flex items-center gap-2 rounded-full bg-white/10 px-3 py-1.5 text-white/90 hover:bg-white/15 transition"
                >
                  <Avatar size="h-7 w-7" textSize="text-xs" />
                  <span className="max-w-[120px] truncate">{auth?.name ?? t("navbar.user")}</span>
                  {unread > 0 && <span className="ml-1 h-2 w-2 rounded-full bg-[#98dea3]" />}
                </button>

                {menuOpen && (
                  <div className="absolute right-0 mt-2 w-56 rounded-xl bg-[#32384a] p-1 shadow-xl ring-1 ring-black/20">
                    <Link to="/dashboard"  className="block rounded-lg px-3 py-2 text-sm text-white/90 hover:bg-white/10" onClick={() => setMenuOpen(false)}>{t("navbar.dashboard")}</Link>
                    {(auth?.role || "").toUpperCase() === "TUTOR" && (
                      <Link to="/onboarding/tutor" className="block rounded-lg px-3 py-2 text-sm text-white/90 hover:bg-white/10" onClick={() => setMenuOpen(false)}>
                        {t("navbar.myProfile")}
                      </Link>
                    )}
                    {(auth?.role || "").toUpperCase() === "TUTOR" ? (
                      <Link to="/bookings" className="block rounded-lg px-3 py-2 text-sm text-white/90 hover:bg-white/10" onClick={() => setMenuOpen(false)}>
                        {t("navbar.myBookings")}
                      </Link>
                    ) : (
                      <Link to="/my-requests" className="block rounded-lg px-3 py-2 text-sm text-white/90 hover:bg-white/10" onClick={() => setMenuOpen(false)}>
                        {t("navbar.myRequests", { defaultValue: "Мої запити" })}
                      </Link>
                    )}
                    <Link to="/messages"   className="flex items-center justify-between rounded-lg px-3 py-2 text-sm text-white/90 hover:bg-white/10" onClick={() => setMenuOpen(false)}>
                      <span>{t("navbar.messages", { defaultValue: "Messages" })}</span><UnreadBadge count={unread} />
                    </Link>
                    {isAdmin && (
                      <Link to="/admin" className="block rounded-lg px-3 py-2 text-sm text-white/90 hover:bg-white/10" onClick={() => setMenuOpen(false)}>{t("navbar.admin")}</Link>
                    )}
                    <Link to="/account" className="block rounded-lg px-3 py-2 text-sm text-white/90 hover:bg-white/10" onClick={() => setMenuOpen(false)}>
                      {t("navbar.accountSettings")}
                    </Link>
                    <div className="my-1 border-t border-white/10" />
                    <button onClick={handleLogout} className="block w-full rounded-lg px-3 py-2 text-left text-sm text-red-300 hover:bg-white/10">
                      {t("navbar.logout")}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link to="/login" className="rounded-xl border border-white/20 px-4 py-1.5 text-sm text-white/90 hover:bg-white/10 transition">
                {t("navbar.signIn")}
              </Link>
            )}
          </nav>

          {/* ── Mobile right side ────────────────────────────────────── */}
          <div className="ml-auto flex items-center gap-3 md:hidden">
            {/* Unread dot on mobile */}
            {isAuthed && unread > 0 && (
              <span className="h-2 w-2 rounded-full bg-[#98dea3]" />
            )}
            <LanguageSwitcher />
            <button
              onClick={() => setMobileOpen((p) => !p)}
              className="rounded-lg p-2 text-white/80 hover:bg-white/10 transition"
              aria-label="Toggle menu"
            >
              <HamburgerIcon open={mobileOpen} />
            </button>
          </div>
        </div>
      </header>

      {/* ── Mobile drawer overlay ──────────────────────────────────────── */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/50 md:hidden"
          onClick={closeMobile}
        />
      )}

      {/* ── Mobile drawer panel ────────────────────────────────────────── */}
      <div
        className={[
          "fixed top-0 right-0 z-50 h-full w-72 max-w-[85vw] bg-[#272c39] shadow-2xl transition-transform duration-300 md:hidden flex flex-col",
          // `invisible` (delayed until the slide-out transition ends) +
          // pointer-events-none keep the parked drawer from being tabbable,
          // clickable, or contributing to horizontal page overflow.
          mobileOpen
            ? "translate-x-0"
            : "translate-x-full invisible pointer-events-none [transition:transform_300ms,visibility_0s_300ms]",
        ].join(" ")}
      >
        {/* Drawer header */}
        <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">
          <Link to="/" onClick={closeMobile}>
            <img src={logo} alt="Profio" className="h-9 w-auto" />
          </Link>
          <button onClick={closeMobile} className="rounded-lg p-2 text-white/70 hover:bg-white/10 transition">
            <HamburgerIcon open={true} />
          </button>
        </div>

        {/* Drawer content */}
        <nav className="flex-1 overflow-y-auto px-4 py-4 space-y-1">
          {isAuthed ? (
            <>
              {/* User info */}
              <div className="mb-4 flex items-center gap-3 rounded-xl bg-white/5 px-4 py-3">
                <Avatar size="h-9 w-9" textSize="text-sm" />
                <span className="truncate text-sm font-medium text-white">{auth?.name ?? t("navbar.user")}</span>
              </div>

              <Link to="/dashboard" onClick={closeMobile} className="flex items-center gap-3 rounded-xl px-4 py-3 text-white/80 hover:bg-white/10 hover:text-white transition">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>
                {t("navbar.dashboard")}
              </Link>

              {(auth?.role || "").toUpperCase() === "TUTOR" && (
                <Link to="/onboarding/tutor" onClick={closeMobile} className="flex items-center gap-3 rounded-xl px-4 py-3 text-white/80 hover:bg-white/10 hover:text-white transition">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>
                  {t("navbar.myProfile")}
                </Link>
              )}

              {(auth?.role || "").toUpperCase() === "TUTOR" ? (
                <Link to="/bookings" onClick={closeMobile} className="flex items-center gap-3 rounded-xl px-4 py-3 text-white/80 hover:bg-white/10 hover:text-white transition">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                  {t("navbar.myBookings")}
                </Link>
              ) : (
                <Link to="/my-requests" onClick={closeMobile} className="flex items-center gap-3 rounded-xl px-4 py-3 text-white/80 hover:bg-white/10 hover:text-white transition">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                  {t("navbar.myRequests", { defaultValue: "Мої запити" })}
                </Link>
              )}

              <Link to="/messages" onClick={closeMobile} className="flex items-center justify-between rounded-xl px-4 py-3 text-white/80 hover:bg-white/10 hover:text-white transition">
                <span className="flex items-center gap-3">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                  {t("navbar.messages", { defaultValue: "Messages" })}
                </span>
                <UnreadBadge count={unread} />
              </Link>

              <Link to="/account" onClick={closeMobile} className="flex items-center gap-3 rounded-xl px-4 py-3 text-white/80 hover:bg-white/10 hover:text-white transition">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
                {t("navbar.accountSettings")}
              </Link>

              <div className="border-t border-white/10 pt-2 mt-2" />

              {/* "Find a specialist" is a client action — hidden for specialists
                  (a specialist doesn't search for other specialists). */}
              {(auth?.role || "").toUpperCase() !== "TUTOR" && (
                <Link to="/search" onClick={closeMobile} className="flex items-center gap-3 rounded-xl px-4 py-3 text-white/80 hover:bg-white/10 hover:text-white transition">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                  {t("navbar.findTutor")}
                </Link>
              )}

              <Link to="/about" onClick={closeMobile} className="flex items-center gap-3 rounded-xl px-4 py-3 text-white/80 hover:bg-white/10 hover:text-white transition">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                {t("navbar.about")}
              </Link>

              <Link to="/contact" onClick={closeMobile} className="flex items-center gap-3 rounded-xl px-4 py-3 text-white/80 hover:bg-white/10 hover:text-white transition">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                {t("navbar.contact")}
              </Link>

              {isAdmin && (
                <>
                  <div className="border-t border-white/10 pt-2 mt-2" />
                  <Link to="/admin" onClick={closeMobile} className="flex items-center gap-3 rounded-xl px-4 py-3 text-[#98dea3] hover:bg-white/10 transition">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                    {t("navbar.adminPanel")}
                  </Link>
                </>
              )}
            </>
          ) : (
            <>
              <Link to="/search" onClick={closeMobile} className="flex items-center gap-3 rounded-xl px-4 py-3 text-white/80 hover:bg-white/10 hover:text-white transition">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                {t("navbar.findTutor")}
              </Link>
              <Link to="/about" onClick={closeMobile} className="flex items-center gap-3 rounded-xl px-4 py-3 text-white/80 hover:bg-white/10 hover:text-white transition">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                {t("navbar.about")}
              </Link>
              <Link to="/contact" onClick={closeMobile} className="flex items-center gap-3 rounded-xl px-4 py-3 text-white/80 hover:bg-white/10 hover:text-white transition">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                {t("navbar.contact")}
              </Link>
            </>
          )}
        </nav>

        {/* Drawer footer */}
        <div className="border-t border-white/10 px-4 py-4">
          {isAuthed ? (
            <button
              onClick={handleLogout}
              className="flex w-full items-center gap-3 rounded-xl px-4 py-3 text-red-400 hover:bg-white/10 transition"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
              {t("navbar.logout")}
            </button>
          ) : (
            <Link
              to="/login"
              onClick={closeMobile}
              className="block w-full rounded-xl bg-[#7269e3] px-4 py-3 text-center text-sm font-semibold text-white hover:opacity-90 transition"
            >
              {t("navbar.signIn")}
            </Link>
          )}
        </div>
      </div>
    </>
  );
}
